/**
 * Yahoo Fantasy OAuth 2.0 – PKCE flow
 *
 * The Yahoo Fantasy API requires OAuth 2.0.  Because this app is a pure
 * client-side SPA, we use the Authorization Code + PKCE flow so no client
 * secret is needed in the bundle.
 *
 * Required app registration at
 * https://developer.yahoo.com/apps/create/
 *   • Application Type: Web Application
 *   • Callback Domain: the origin the app runs on
 *   • API Permissions: Fantasy Sports (Read)
 *
 * Set the env var VITE_YAHOO_CLIENT_ID to your app's consumer key.
 */

const YAHOO_AUTH_URL = 'https://api.login.yahoo.com/oauth2/request_auth';
const YAHOO_TOKEN_URL = 'https://api.login.yahoo.com/oauth2/get_token';
const REDIRECT_PATH = '/';

const clientId = () => import.meta.env.VITE_YAHOO_CLIENT_ID || '';

const getRedirectUri = () => `${window.location.origin}${REDIRECT_PATH}`;

// ─── PKCE helpers ────────────────────────────────────────────────────────────

const base64UrlEncode = (buffer) =>
  btoa(String.fromCharCode(...new Uint8Array(buffer)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');

const generateCodeVerifier = () => {
  const array = new Uint8Array(64);
  crypto.getRandomValues(array);
  return base64UrlEncode(array);
};

const deriveCodeChallenge = async (verifier) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(digest);
};

// ─── State management in sessionStorage ──────────────────────────────────────

const STATE_KEY = 'yahoo_oauth_state';
const VERIFIER_KEY = 'yahoo_oauth_verifier';

const storeOAuthState = (state, verifier) => {
  sessionStorage.setItem(STATE_KEY, state);
  sessionStorage.setItem(VERIFIER_KEY, verifier);
};

const consumeOAuthState = () => {
  const state = sessionStorage.getItem(STATE_KEY);
  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  sessionStorage.removeItem(STATE_KEY);
  sessionStorage.removeItem(VERIFIER_KEY);
  return { state, verifier };
};

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Begin the OAuth dance.  Redirects the current tab away from the app.
 */
export const redirectToYahooLogin = async () => {
  const id = clientId();
  if (!id) {
    throw new Error(
      'VITE_YAHOO_CLIENT_ID is not set. Add your Yahoo app consumer key as an environment variable.'
    );
  }

  const verifier = generateCodeVerifier();
  const challenge = await deriveCodeChallenge(verifier);
  const state = base64UrlEncode(crypto.getRandomValues(new Uint8Array(16)));

  storeOAuthState(state, verifier);

  const params = new URLSearchParams({
    client_id: id,
    redirect_uri: getRedirectUri(),
    response_type: 'code',
    state,
    code_challenge: challenge,
    code_challenge_method: 'S256',
  });

  window.location.href = `${YAHOO_AUTH_URL}?${params}`;
};

/**
 * Given the OAuth callback URL params, exchange the code for tokens.
 * Returns the token response object (access_token, refresh_token, expires_in, …).
 * Throws if state mismatch or exchange fails.
 */
export const handleYahooCallback = async (searchParams) => {
  const code = searchParams.get('code');
  const returnedState = searchParams.get('state');
  const errorParam = searchParams.get('error');

  if (errorParam) {
    throw new Error(`Yahoo denied access: ${errorParam}`);
  }
  if (!code) {
    throw new Error('No authorization code returned from Yahoo.');
  }

  const { state: savedState, verifier } = consumeOAuthState();

  if (!savedState || returnedState !== savedState) {
    throw new Error('OAuth state mismatch – possible CSRF attempt.');
  }

  const body = new URLSearchParams({
    client_id: clientId(),
    redirect_uri: getRedirectUri(),
    grant_type: 'authorization_code',
    code,
    code_verifier: verifier,
  });

  const response = await fetch(YAHOO_TOKEN_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Yahoo token exchange failed (${response.status}): ${text}`);
  }

  return response.json();
};

/**
 * Use the stored refresh token to obtain a fresh access token.
 * Returns the new token response object.
 */
export const refreshYahooToken = async (refreshToken) => {
  const body = new URLSearchParams({
    client_id: clientId(),
    redirect_uri: getRedirectUri(),
    grant_type: 'refresh_token',
    refresh_token: refreshToken,
  });

  const response = await fetch(YAHOO_TOKEN_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Yahoo token refresh failed (${response.status}): ${text}`);
  }

  return response.json();
};

/**
 * True when the current URL contains Yahoo OAuth callback parameters.
 */
export const isYahooCallback = (searchParams) =>
  searchParams.has('code') && searchParams.has('state');
